// src/pages/admin/AdminProducts.js
import React, { useEffect, useState, useMemo } from 'react';
import {
  Container, Typography, Button, Box, Paper,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TablePagination,
  TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, Stack
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Link } from 'react-router-dom';
import axiosInstance from '../../services/api';
import { getFullImageUrl } from '../../utils/urlHelpers';

const trUp = (s) => (s || '').toLocaleUpperCase('tr-TR');

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [subTypes, setSubTypes] = useState([]);
  const [search, setSearch] = useState('');
  const [selType, setSelType] = useState('');
  const [selSubType, setSelSubType] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => { fetchProducts(); }, []);
  useEffect(() => {
    axiosInstance.get('/productsubtypes')
      .then(({ data }) => setSubTypes(data.map(st => ({
        id: st.id, name: st.name,
        typeId: st.productTypeId || st.productTypeDto?.id,
        typeName: st.productTypeDto?.name || 'Diğer',
      }))))
      .catch(() => {});
  }, []);

  const fetchProducts = async () => {
    try {
      const { data } = await axiosInstance.get('/products');
      setProducts(data);
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu ürünü silmek istediğinize emin misiniz?')) return;
    try {
      await axiosInstance.delete(`/products/${id}`);
      fetchProducts();
    } catch (err) { console.error(err); }
  };

  const types = useMemo(() => {
    const map = new Map();
    subTypes.forEach(st => { if (st.typeId && !map.has(st.typeId)) map.set(st.typeId, st.typeName); });
    return [...map.entries()].map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  }, [subTypes]);

  const subTypesOfType = useMemo(
    () => subTypes.filter(st => st.typeId === selType).sort((a, b) => a.name.localeCompare(b.name, 'tr')),
    [subTypes, selType]
  );

  const filtered = useMemo(() => {
    let list = products;
    const q = trUp(search).trim();
    if (q) list = list.filter(p => trUp(p.name).includes(q) || trUp(p.oemCode).includes(q));
    if (selSubType) list = list.filter(p => p.productSubTypeId === selSubType);
    else if (selType) list = list.filter(p => p.productSubTypeDto?.productTypeId === selType);
    return list;
  }, [products, search, selType, selSubType]);

  useEffect(() => { setPage(0); }, [search, selType, selSubType, rowsPerPage]);

  const pageItems = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const hasFilters = search || selType || selSubType;

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Ürün Yönetimi</Typography>
        <Button variant="contained" color="primary" component={Link} to="/admin/products/new">
          Yeni Ürün Ekle
        </Button>
      </Box>

      {/* Filtre çubuğu */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Ürün adı veya OEM kodu ara…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ flex: '1 1 240px', minWidth: 200 }}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Kategori</InputLabel>
            <Select label="Kategori" value={selType} onChange={e => { setSelType(e.target.value); setSelSubType(''); }}>
              <MenuItem value="">Tümü</MenuItem>
              {types.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }} disabled={!selType}>
            <InputLabel>Alt Kategori</InputLabel>
            <Select label="Alt Kategori" value={selSubType} onChange={e => setSelSubType(e.target.value)}>
              <MenuItem value="">Tümü</MenuItem>
              {subTypesOfType.map(st => <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>)}
            </Select>
          </FormControl>
          {hasFilters && <Button variant="text" onClick={() => { setSearch(''); setSelType(''); setSelSubType(''); }}>Temizle</Button>}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          <strong>{filtered.length}</strong> ürün
        </Typography>
      </Paper>

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ width: 56 }}>Görsel</TableCell>
              <TableCell>Ürün Adı</TableCell>
              <TableCell sx={{ width: 140 }}>OEM Kodu</TableCell>
              <TableCell sx={{ width: 170 }}>Kategori</TableCell>
              <TableCell align="right" sx={{ width: 160 }}>İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageItems.map(product => (
              <TableRow key={product.id} hover>
                <TableCell>
                  {product.imageUrl ? (
                    <img
                      src={getFullImageUrl(product.imageUrl)}
                      alt={product.name}
                      style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 4, display: 'block' }}
                    />
                  ) : (
                    <Box sx={{ width: 44, height: 44, bgcolor: 'grey.200', borderRadius: 1 }} />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{product.name}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{product.oemCode || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{product.productSubTypeDto?.name || '—'}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" variant="outlined" component={Link} to={`/admin/products/edit/${product.id}`}>
                      Düzenle
                    </Button>
                    <Button size="small" variant="outlined" color="error" onClick={() => handleDelete(product.id)}>
                      Sil
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {pageItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                  Ürün bulunamadı.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => setRowsPerPage(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Sayfa başına:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
        />
      </TableContainer>
    </Container>
  );
};

export default AdminProducts;
